if (!customElements.get('cart-tier-progress')) {
  class CartTierProgress extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      try {
        this.tiers = JSON.parse(this.dataset.tiers || '[]');
      } catch (_error) {
        this.tiers = [];
      }
      // Settings are four free-form number fields; nothing stops a merchant
      // entering them out of order, and the max/next lookups assume ascending.
      this.tiers = this.tiers
        .filter((tier) => tier && Number.isFinite(tier.qty) && tier.qty > 0)
        .sort((a, b) => a.qty - b.qty);
      if (!this.tiers.length) return;

      this.count = Number.parseInt(this.dataset.count || '0', 10) || 0;
      this.productCounts = this.dataset.productCounts === 'true';

      // 'preview' is the resting state. 'unlocked' is the short beat right
      // after an add-to-cart landed, and is the only phase in which this box
      // is allowed to say a discount is unlocked.
      this.phase = 'preview';
      this.commitPending = false;

      // The stepper is a SIBLING of this element — both sit inside
      // .cart-tier-progress-wrap — so a listener on `this` would never see it.
      // On the vso product sections the stepper lives outside the wrap and
      // this query stays empty: the box then behaves exactly as before and
      // shows the real cart only.
      this.wrap = this.closest('.cart-tier-progress-wrap');
      // Rendered straight away, with no debounce: the shopper is holding the
      // +/- button and expects the bar to answer immediately.
      this.onQuantityChange = (event) => {
        if (event.target?.name !== 'quantity') return;
        this.render();
      };
      if (this.wrap) {
        // change fires for the +/- buttons and for typing then leaving the
        // field; input covers the keystrokes in between.
        this.wrap.addEventListener('change', this.onQuantityChange);
        this.wrap.addEventListener('input', this.onQuantityChange);
      }

      // Deliberately ignores any count carried on the event. The vso product
      // sections put the number of units JUST ADDED in detail.data.itemCount,
      // not the cart total, so reading a number here and trusting it is how
      // this module would end up double counting. The event is a signal only;
      // syncCount goes and asks a source that knows the total.
      this.onCartUpdate = () => this.syncCount();
      this.onWindowRefresh = () => this.syncCount();

      document.addEventListener('cart:update', this.onCartUpdate);
      window.addEventListener('pageshow', this.onWindowRefresh);
      window.addEventListener('focus', this.onWindowRefresh);

      // The vendor bundle (js.shrinetheme.com/js/v2/main.js, obfuscated) is
      // Dawn-derived and publishes on its own pub/sub after every successful
      // cart write. That is the one signal telling us an add-to-cart
      // SUCCEEDED — the bubble observer below can see the count move but not
      // why it moved. Guarded twice: PUB_SUB_EVENTS is a bare global const in
      // that bundle rather than a window property, and the bundle is a
      // third-party script that may not have loaded at all.
      let cartChannel = 'cart-update';
      try {
        if (PUB_SUB_EVENTS && PUB_SUB_EVENTS.cartUpdate) cartChannel = PUB_SUB_EVENTS.cartUpdate;
      } catch (_error) {
        // Vendor bundle missing; the listeners above still cover every path.
      }
      if (typeof window.subscribe === 'function') {
        this.cartUnsubscriber = window.subscribe(cartChannel, (payload) => {
          // Only an add from a product form earns the confirmation beat. The
          // 'cart-items' source is a quantity edit made inside the cart.
          if ((payload?.source || '').indexOf('product-form') === 0) this.commitPending = true;
          this.syncCount();
        });
      }

      // The header bubble is rewritten by every cart path, including the
      // vendor bundle's own add-to-cart, so it doubles as a change signal.
      // #cart-icon-bubble is the stable wrapper that survives those swaps.
      const bubbleHost = document.getElementById('cart-icon-bubble');
      if (bubbleHost) {
        this.bubbleObserver = new MutationObserver(() => this.syncCount());
        this.bubbleObserver.observe(bubbleHost, { childList: true, subtree: true, characterData: true });
      }

      // The server already painted the real-cart state; this first render adds
      // the projected layer for the quantity the stepper opens on.
      this.render();
    }

    disconnectedCallback() {
      document.removeEventListener('cart:update', this.onCartUpdate);
      window.removeEventListener('pageshow', this.onWindowRefresh);
      window.removeEventListener('focus', this.onWindowRefresh);
      this.wrap?.removeEventListener('change', this.onQuantityChange);
      this.wrap?.removeEventListener('input', this.onQuantityChange);
      this.cartUnsubscriber?.();
      this.cartUnsubscriber = null;
      this.bubbleObserver?.disconnect();
      this.bubbleObserver = null;
      clearTimeout(this.syncTimer);
      clearTimeout(this.phaseTimer);
      delete this.dataset.ready;
    }

    readBubbleCount() {
      const host = document.getElementById('cart-icon-bubble');
      if (!host) return null;
      const bubble = host.querySelector('.cart-count-bubble');
      if (!bubble) return 0;
      const text = bubble.querySelector('span[aria-hidden="true"]')?.textContent?.trim();
      // The count span is omitted at 100+ items; force a real fetch then.
      if (!text || !/^\d+$/.test(text)) return null;
      return Number.parseInt(text, 10);
    }

    async refetchCount() {
      try {
        const root = window.Shopify?.routes?.root || '/';
        const response = await fetch(`${root}cart.js`, {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!response.ok) return;
        const cart = await response.json();
        // Note the gap: the cart JSON carries no product tags, so this total
        // still includes cart-hidden items. Only reached when the bubble has
        // no readable number, i.e. a cart of 100+ items.
        this.applyCount(cart.item_count || 0);
      } catch (_error) {
        // Keep the last known state rather than flashing a wrong tier.
      }
    }

    // The header bubble is the source of truth for the eligible count: it is
    // server-rendered from the same rule as this box (header.liquid's
    // non_upsell_count, which drops cart-hidden items) and /cart.js cannot
    // reproduce it, because the cart JSON has no product tags. Debounced
    // because one cart write moves several things at once, and because the
    // vendor's publish can land a beat before it re-renders the bubble.
    syncCount() {
      clearTimeout(this.syncTimer);
      this.syncTimer = setTimeout(() => {
        const bubbleCount = this.readBubbleCount();
        if (bubbleCount == null) {
          void this.refetchCount();
        } else {
          this.applyCount(bubbleCount);
        }
      }, 200);
    }

    // The only place this.count is written, and it is always ASSIGNED a number
    // a server-rendered source reported — never incremented by anything this
    // page added. That is what makes double counting structurally impossible
    // rather than merely unlikely.
    applyCount(count) {
      const changed = count !== this.count;
      this.count = count;

      const earned = this.commitPending && changed && this.getTierBoundaries(count).unlocked;
      this.commitPending = false;
      if (earned) {
        clearTimeout(this.phaseTimer);
        this.phase = 'unlocked';
        this.phaseTimer = setTimeout(() => {
          this.phase = 'preview';
          this.render();
        }, 2800);
      }

      // Always render. An unchanged cart count is not a reason to skip: the
      // stepper may have moved since the last paint.
      this.render();
    }

    // Read back from the DOM on every render and never accumulated, so the
    // units the shopper picked cannot survive into the committed state. Zero
    // when the product does not count toward tiers (cart-hidden) or when this
    // template renders its stepper outside the wrap.
    get pendingQty() {
      if (!this.productCounts || !this.wrap) return 0;
      const input = this.wrap.querySelector('input[name="quantity"]:not([type="hidden"])');
      if (!input) return 0;
      const value = Number.parseInt(input.value, 10);
      return Number.isFinite(value) && value > 0 ? value : 0;
    }

    getTierBoundaries(count) {
      const first = this.tiers[0];
      const max = this.tiers[this.tiers.length - 1];
      let unlocked = null;
      let next = null;
      this.tiers.forEach((tier) => {
        if (tier.qty <= count) unlocked = tier;
        if (tier.qty > count && !next) next = tier;
      });
      return { first, max, unlocked, next };
    }

    // The real-cart branches mirror the Liquid in
    // snippets/cart-tier-progress.liquid — any copy change must be made in
    // both places. The stepper branch has no Liquid twin on purpose: the
    // server cannot know what the shopper picked.
    //
    // Three sentences, no more. The figure in the common one is the gap
    // between the next tier and the PROJECTED count — the cart plus whatever
    // the quantity stepper holds — so it falls by one on every press of +.
    // That movement is the point: the line always states the work still left,
    // never a total the shopper has to subtract from in their head.
    //
    // Only a cart that really holds the units may use the word "unlocked".
    // Everything short of that is phrased as an instruction ("Add …"), so
    // nothing on screen claims a discount the cart has not earned.
    //
    // "OFF" is uppercase, matching the milestone tiles, so the sentence and
    // the tile below it read as the same offer rather than two.
    // Free shipping is never named; the chip at the top of the box states it.
    // Returns HTML, and the reward figure is the bold part.
    getMessage(count, projected) {
      const { max, unlocked } = this.getTierBoundaries(count);
      // The tier above the PROJECTED count, not the cart. That is what makes
      // the figure tick down by one on every press of +, which is the whole
      // point of the set: the sentence always states the work still left.
      const ahead = this.getTierBoundaries(projected);

      // The units really are in the cart, so this is the only branch allowed
      // to say a discount is unlocked.
      if (this.phase === 'unlocked' && unlocked) {
        return { state: 'unlocked', text: `<strong>✓ ${unlocked.pct}% OFF</strong> unlocked` };
      }

      // The workhorse, and nearly always the one on screen.
      if (ahead.next) {
        const need = ahead.next.qty - projected;
        return {
          state: 'next',
          text: `Add <strong>${need} item${need === 1 ? '' : 's'}</strong> to unlock <strong>${ahead.next.pct}% OFF</strong>`,
        };
      }

      // Past the top tier with nothing left to count. Either the cart is
      // already there, or the stepper holds enough and one press of the
      // button finishes it.
      if (count >= max.qty) {
        // Same sentence as the beat above, different badge: the trophy is how
        // the best tier has always read, and it is the resting state here
        // rather than a beat, so it keeps its own class.
        return { state: 'max', text: `<strong>✓ ${max.pct}% OFF</strong> unlocked` };
      }
      return { state: 'preview', text: `Add to cart for <strong>${max.pct}% OFF</strong>` };
    }

    // Milestones sit at equal spacing (the centres of the four tiles); the
    // fill is interpolated by item count between them. Same rule as Liquid.
    getFillPercent(count) {
      const total = this.tiers.length;
      if (!total || count <= 0) return 0;
      if (count >= this.tiers[total - 1].qty) return 100;
      let prevQty = 0;
      let prevPos = 0;
      for (let index = 0; index < total; index += 1) {
        const tier = this.tiers[index];
        const pos = ((index + 0.5) / total) * 100;
        if (count <= tier.qty) {
          return prevPos + ((count - prevQty) / (tier.qty - prevQty)) * (pos - prevPos);
        }
        prevQty = tier.qty;
        prevPos = pos;
      }
      return 100;
    }

    render() {
      // Two numbers, and they are never mixed up: this.count is the cart,
      // projected is the cart plus what the stepper is holding.
      const pending = this.pendingQty;
      const projected = this.count + pending;

      const message = this.getMessage(this.count, projected);
      const box = this.querySelector('[data-tier-message]');
      if (box) {
        box.classList.remove('is-max', 'is-first', 'is-next', 'is-preview', 'is-unlocked');
        box.classList.add(`is-${message.state}`);
      }
      const text = this.querySelector('[data-tier-message-text]');
      if (text) text.innerHTML = message.text;

      // The bar and the milestones are drawn from the PROJECTED count, so a
      // tier the stepper would reach looks exactly like one the cart already
      // earned. Only the gold "Applied" tile still keys off the real cart, so
      // nothing on screen claims a discount is live when it is not.
      const fill = this.querySelector('[data-tier-fill]');
      if (fill) fill.style.width = `${this.getFillPercent(projected).toFixed(2)}%`;

      this.querySelectorAll('[data-tier-dot]').forEach((dot) => {
        const qty = Number.parseInt(dot.dataset.tierDot || '0', 10);
        dot.classList.toggle('is-done', projected >= qty);
      });

      // The solid accent tile follows the PROJECTED count, so raising the
      // stepper walks it up the row. There is always exactly one, and it sits
      // on the tier the shopper is heading for rather than the one the cart
      // has already reached.
      const { unlocked } = this.getTierBoundaries(projected);
      this.querySelectorAll('.cart-tier-progress__milestone').forEach((milestone) => {
        const qty = Number.parseInt(milestone.dataset.tierQty || '0', 10);
        let state = 'upcoming';
        if (unlocked && unlocked.qty === qty) state = 'applied';
        else if (projected >= qty) state = 'completed';

        milestone.classList.remove('is-completed', 'is-applied', 'is-upcoming', 'is-current');
        milestone.classList.add(`is-${state}`);
        // aria-current marks where the shopper actually is, which is the tier
        // the cart reached — not the one the solid tile has walked up to.
        if (state === 'applied' && this.count >= qty) {
          milestone.setAttribute('aria-current', 'step');
        } else {
          milestone.removeAttribute('aria-current');
        }

        // The label stays honest where the tile no longer does. What decides
        // the wording is the real cart, not the tile's appearance: a tier the
        // stepper merely points at is announced as "after adding", never as
        // applied or unlocked.
        const inCart = this.count >= qty;
        const baseLabel = (milestone.getAttribute('aria-label') || '').replace(/ — (unlocked|applied now|after adding)$/, '');
        let suffix = '';
        if (state === 'applied') suffix = inCart ? ' — applied now' : ' — after adding';
        else if (state === 'completed') suffix = inCart ? ' — unlocked' : ' — after adding';
        milestone.setAttribute('aria-label', baseLabel + suffix);
      });
    }
  }

  customElements.define('cart-tier-progress', CartTierProgress);
}
